import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { accountApi } from "../../lib/account";
import type {
  UserNotificationListResponseDto,
  UserNotificationSummaryResponseDto,
  UserNotificationViewDto,
} from "../../types/types";
import Button from "../ui/Button/Button";
import {
  buildDashboardNotificationPreviewEntries,
  type DashboardNotificationPreviewEntry,
} from "./dashboardNotifications";

const NOTIFICATION_PREVIEW_COUNT = 3;
const NOTIFICATION_PREVIEW_SCAN_SIZE = 100;

const getNotificationsHref = (view: UserNotificationViewDto) =>
  view === "recent-updates" ? "/notifications?tab=recent-updates" : "/notifications";

const adjustNotificationSummaryUnreadCounts = (
  current: UserNotificationSummaryResponseDto,
  view: UserNotificationViewDto,
  unreadDelta: number,
): UserNotificationSummaryResponseDto => ({
  ...current,
  unreadCount: Math.max(0, current.unreadCount - unreadDelta),
  unreadNeedsActionCount: view === "needs-action"
    ? Math.max(0, current.unreadNeedsActionCount - unreadDelta)
    : current.unreadNeedsActionCount,
  unreadRecentUpdatesCount: view === "recent-updates"
    ? Math.max(0, current.unreadRecentUpdatesCount - unreadDelta)
    : current.unreadRecentUpdatesCount,
});

const DashboardNotificationPreviewItem = ({
  entry,
  pendingReadEntryId,
  onMarkRead,
  onOpen,
}: {
  entry: DashboardNotificationPreviewEntry;
  pendingReadEntryId: string | null;
  onMarkRead: (entry: DashboardNotificationPreviewEntry) => void;
  onOpen: (entry: DashboardNotificationPreviewEntry) => void;
}) => {
  const isUnread = entry.notifications.some((notification) => notification.readAt === null);
  const isReadPending = pendingReadEntryId === entry.id;

  return (
    <li className="rounded-md border border-myteengig-border bg-myteengig-background/70 px-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-semibold leading-5 text-myteengig-text-primary">{entry.title}</p>
        </div>
        <span
          className={`w-fit shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            entry.needsAction
              ? "bg-red-50 text-red-700"
              : "bg-myteengig-primary/10 text-myteengig-primary"
          }`}
        >
          {entry.needsAction ? "Action" : "Update"}
        </span>
      </div>
      <p className="mt-1 break-words text-xs leading-5 text-myteengig-text-muted">{entry.body}</p>
      <div className="mt-2 flex gap-2 sm:justify-end">
        {isUnread ? (
          <Button
            variant="tertiary"
            size="xs"
            disabled={isReadPending}
            onClick={() => onMarkRead(entry)}
            className="min-h-8 min-w-0 flex-1 whitespace-normal border border-myteengig-primary/15 bg-myteengig-primary/5 px-2.5 text-center leading-tight hover:bg-myteengig-primary/10 disabled:opacity-70 sm:flex-none"
          >
            {isReadPending ? "Saving..." : "Mark Read"}
          </Button>
        ) : null}
        <Button
          variant={entry.needsAction ? "primary" : "outline"}
          size="xs"
          disabled={isReadPending}
          onClick={() => onOpen(entry)}
          className="min-h-8 min-w-0 flex-1 whitespace-normal px-2.5 text-center leading-tight disabled:opacity-70 sm:flex-none"
        >
          {entry.ctaLabel}
        </Button>
      </div>
    </li>
  );
};

type MarkDashboardNotificationsReadPayload = {
  entryId: string;
  notificationIds: string[];
};

const DashboardNotificationsPreview = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notificationsSummaryQuery = useQuery({
    queryKey: ["notifications-summary"],
    queryFn: () => accountApi.getNotificationSummary(),
    staleTime: 30_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    retry: 1,
  });
  const summary = notificationsSummaryQuery.data;
  const previewView: UserNotificationViewDto =
    (summary?.unreadNeedsActionCount ?? 0) > 0 ? "needs-action" : "recent-updates";
  const unreadCountForPreviewView = previewView === "needs-action"
    ? summary?.unreadNeedsActionCount ?? 0
    : summary?.unreadRecentUpdatesCount ?? 0;
  const totalUnreadCount = summary?.unreadCount ?? 0;
  const notificationsPreviewQueryKey = ["notifications", previewView, 0, NOTIFICATION_PREVIEW_SCAN_SIZE] as const;

  const notificationsPreviewQuery = useQuery({
    queryKey: notificationsPreviewQueryKey,
    queryFn: () => accountApi.listNotifications({
      view: previewView,
      page: 0,
      size: NOTIFICATION_PREVIEW_SCAN_SIZE,
    }),
    enabled: !notificationsSummaryQuery.isLoading && unreadCountForPreviewView > 0,
    staleTime: 30_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    retry: 1,
  });

  const invalidateNotificationQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
    queryClient.invalidateQueries({ queryKey: ["notifications-summary"] });
  };

  const markReadMutation = useMutation({
    mutationFn: ({ notificationIds }: MarkDashboardNotificationsReadPayload) =>
      Promise.all(notificationIds.map((notificationId) => accountApi.markNotificationRead(notificationId))),
    onMutate: async ({ notificationIds }: MarkDashboardNotificationsReadPayload) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });
      await queryClient.cancelQueries({ queryKey: ["notifications-summary"] });

      const previousPreview = queryClient.getQueryData<UserNotificationListResponseDto>(notificationsPreviewQueryKey);
      const previousSummary = queryClient.getQueryData<UserNotificationSummaryResponseDto>(["notifications-summary"]);
      const notificationIdsToMarkRead = new Set(notificationIds);
      const unreadCountToMarkRead = previousPreview?.notifications.filter(
        (notification) => notificationIdsToMarkRead.has(notification.id) && notification.readAt === null,
      ).length ?? 0;
      const now = new Date().toISOString();

      queryClient.setQueryData<UserNotificationListResponseDto | undefined>(
        notificationsPreviewQueryKey,
        (current) =>
          current
            ? {
                ...current,
                notifications: current.notifications.map((notification) =>
                  notificationIdsToMarkRead.has(notification.id) && notification.readAt === null
                    ? { ...notification, readAt: now }
                    : notification,
                ),
              }
            : current,
      );

      if (unreadCountToMarkRead > 0) {
        queryClient.setQueryData<UserNotificationSummaryResponseDto | undefined>(
          ["notifications-summary"],
          (current) =>
            current
              ? adjustNotificationSummaryUnreadCounts(current, previewView, unreadCountToMarkRead)
              : current,
        );
      }

      return { previousPreview, previousSummary, previousPreviewQueryKey: notificationsPreviewQueryKey };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previousPreview) {
        queryClient.setQueryData(context.previousPreviewQueryKey, context.previousPreview);
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(["notifications-summary"], context.previousSummary);
      }
    },
    onSettled: invalidateNotificationQueries,
  });

  const previewNotifications = buildDashboardNotificationPreviewEntries(
    (notificationsPreviewQuery.data?.notifications ?? [])
      .filter((notification) => notification.readAt === null),
  ).slice(0, NOTIFICATION_PREVIEW_COUNT);
  const isLoadingPreview = notificationsSummaryQuery.isLoading || notificationsPreviewQuery.isLoading;
  const previewError = notificationsSummaryQuery.error || (unreadCountForPreviewView > 0 ? notificationsPreviewQuery.error : null);
  const pendingReadEntryId = markReadMutation.isPending && markReadMutation.variables
    ? markReadMutation.variables.entryId
    : null;

  const handleMarkRead = (entry: DashboardNotificationPreviewEntry) => {
    markReadMutation.mutate({
      entryId: entry.id,
      notificationIds: entry.notifications.map((notification) => notification.id),
    });
  };

  const handleOpen = async (entry: DashboardNotificationPreviewEntry) => {
    if (entry.notifications.some((notification) => notification.readAt === null)) {
      try {
        await markReadMutation.mutateAsync({
          entryId: entry.id,
          notificationIds: entry.notifications.map((notification) => notification.id),
        });
      } catch {
        // Keep navigation available even if marking read fails.
      }
    }
    navigate(entry.ctaPath);
  };

  return (
    <section className="rounded-lg border border-myteengig-border bg-white p-3 shadow-sm sm:p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Notifications</p>
        </div>
        <Link
          to={getNotificationsHref(previewView)}
          className="inline-flex w-fit shrink-0 self-center items-center justify-center rounded-md border border-myteengig-border bg-white px-2.5 py-1.5 text-center text-xs font-semibold leading-5 text-myteengig-primary transition hover:border-myteengig-primary hover:bg-myteengig-background sm:self-auto"
        >
          All Notifications ({totalUnreadCount})
        </Link>
      </div>

      <div className="mt-3">
        {isLoadingPreview ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-12 animate-pulse rounded-md bg-myteengig-background" />
            ))}
          </div>
        ) : previewError ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            Notifications are temporarily unavailable.
          </p>
        ) : previewNotifications.length > 0 ? (
          <ul className="space-y-2">
            {previewNotifications.map((entry) => (
              <DashboardNotificationPreviewItem
                key={entry.id}
                entry={entry}
                pendingReadEntryId={pendingReadEntryId}
                onMarkRead={handleMarkRead}
                onOpen={handleOpen}
              />
            ))}
          </ul>
        ) : totalUnreadCount > 0 ? (
          <p className="rounded-md border border-myteengig-border bg-myteengig-background/70 px-3 py-2.5 text-sm text-myteengig-text-muted">
            You have unread notifications waiting. Open the full notifications page to review them.
          </p>
        ) : (
          <p className="rounded-md border border-myteengig-border bg-myteengig-background/70 px-3 py-2.5 text-sm text-myteengig-text-muted">
            You are caught up. New action items and updates will appear here.
          </p>
        )}
      </div>
    </section>
  );
};

export default DashboardNotificationsPreview;
